// 关卡系统配置
import i18next from 'i18next';

// 关卡目标类型
export enum LevelGoalType {
  SCORE = 'SCORE',           // 总积分达到指定值
  MAX_NUMBER = 'MAX_NUMBER', // 消除最高数字达到指定值
  // 可扩展的其他目标类型
  MOVES_LIMIT = 'MOVES_LIMIT', // 在指定行动点数内完成目标
  TIME_LIMIT = 'TIME_LIMIT',   // 在指定时间内完成目标
}

// 关卡目标配置
export interface LevelGoal {
  type: LevelGoalType;       // 目标类型
  targetValue: number;       // 目标值
  currentValue?: number;     // 当前值（运行时更新）
  additionalCondition?: {    // 附加条件（可选）
    type: LevelGoalType;     // 附加条件类型
    targetValue: number;     // 附加条件目标值
  };
}

// 关卡配置
export interface LevelConfig {
  id: number;                // 关卡ID
  nameKey: string;           // 关卡名称的翻译键
  descriptionKey: string;    // 关卡描述的翻译键
  goal: LevelGoal;           // 关卡目标
  initialMoves?: number;     // 初始行动点（可选，默认使用全局配置）
  reward?: {                 // 通关奖励（可选）
    diamonds?: number;       // 钻石奖励
    items?: {                // 道具奖励
      type: string;          // 道具类型
      count: number;         // 道具数量
    }[];
  };
}

// 关卡配置列表
export const LEVELS_CONFIG: LevelConfig[] = [
  {
    id: 1,
    nameKey: "levels.level1.name",
    descriptionKey: "levels.level1.description",
    goal: {
      type: LevelGoalType.SCORE,
      targetValue: 100,
    },
    reward: {
      diamonds: 10,
    }
  },
  {
    id: 2,
    nameKey: "levels.level2.name",
    descriptionKey: "levels.level2.description",
    goal: {
      type: LevelGoalType.SCORE,
      targetValue: 200,
    },
    reward: {
      diamonds: 20,
    }
  },
  {
    id: 3,
    nameKey: "levels.level3.name",
    descriptionKey: "levels.level3.description",
    goal: {
      type: LevelGoalType.SCORE,
      targetValue: 800,
    },
    reward: {
      diamonds: 30,
    }
  },
  {
    id: 4,
    nameKey: "levels.level4.name",
    descriptionKey: "levels.level4.description",
    goal: {
      type: LevelGoalType.SCORE,
      targetValue: 1000,
    },
    reward: {
      diamonds: 40,
    }
  },
  {
    id: 5,
    nameKey: "levels.level5.name",
    descriptionKey: "levels.level5.description",
    goal: {
      type: LevelGoalType.MAX_NUMBER,
      targetValue: 9,
    },
    reward: {
      diamonds: 50,
    }
  },
  {
    id: 6,
    nameKey: "levels.level6.name",
    descriptionKey: "levels.level6.description",
    goal: {
      type: LevelGoalType.MAX_NUMBER,
      targetValue: 10,
    },
    reward: {
      diamonds: 60,
    }
  },
  {
    id: 7,
    nameKey: "levels.level7.name",
    descriptionKey: "levels.level7.description",
    goal: {
      type: LevelGoalType.MAX_NUMBER,
      targetValue: 11,
    },
    reward: {
      diamonds: 70,
    }
  },
  {
    id: 8,
    nameKey: "levels.level8.name",
    descriptionKey: "levels.level8.description",
    goal: {
      type: LevelGoalType.MAX_NUMBER,
      targetValue: 12,
    },
    reward: {
      diamonds: 80,
    }
  },
  {
    id: 9,
    nameKey: "levels.level9.name",
    descriptionKey: "levels.level9.description",
    goal: {
      type: LevelGoalType.MAX_NUMBER,
      targetValue: 13,
    },
    reward: {
      diamonds: 90,
    }
  }
];

// 获取指定ID的关卡配置
export const getLevelById = (id: number): LevelConfig | undefined => {
  return LEVELS_CONFIG.find(level => level.id === id);
};

// 获取下一个关卡
export const getNextLevel = (currentLevelId: number): LevelConfig | undefined => {
  const nextLevelIndex = LEVELS_CONFIG.findIndex(level => level.id === currentLevelId) + 1;
  return nextLevelIndex < LEVELS_CONFIG.length ? LEVELS_CONFIG[nextLevelIndex] : undefined;
};

// 新增：获取关卡的本地化名称
export const getLevelName = (level: LevelConfig): string => {
  return i18next.t(level.nameKey);
};

// 获取关卡的本地化描述
export const getLevelDescription = (level: LevelConfig): string => {
  // 根据目标类型动态生成描述
  if (level.goal.type === LevelGoalType.SCORE) {
    return i18next.t('dynamicLevel.scoreGoal', { score: level.goal.targetValue });
  } else if (level.goal.type === LevelGoalType.MAX_NUMBER) {
    return i18next.t('dynamicLevel.numberGoal', { number: level.goal.targetValue });
  } else {
    // 对于未知类型，使用默认的翻译键
    return i18next.t(level.descriptionKey);
  }
}; 